import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthViewModel } from '../ViewModel/AuthViewModel';
import MFAVerificationPopup from '../Components/MFAVerificationPopup';
import { MFAService } from '../Service/MFAService';
import AuthLoadingScreen from '../Components/AuthLoadingScreen';

const GitHubCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleGithubCallback, isLoading } = useAuthViewModel();
  const [showMFAPopup, setShowMFAPopup] = useState(false);
  const [mfaData, setMFAData] = useState<{
    email: string;
    qai: string;
    code: string;
  } | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const code = searchParams.get('code');
    let installation_id_str = searchParams.get('installation_id')
    let installation_id = 0
    if(installation_id_str !==null && installation_id_str !==undefined){
      installation_id=Number(installation_id_str)
    }
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError('GitHub authentication was cancelled or failed');
      setTimeout(() => navigate('/'), 3000);
      return;
    }

    if (code) {
      if(installation_id){
        handleCallback(code, installation_id);
      }else{
        handleCallback(code);
      }
      
    } else {
      setError('No authorization code received');
      setTimeout(() => navigate('/'), 3000);
    }
  }, [searchParams, navigate]);

  const handleCallback = async (code: string, installation_id?:number) => {
    try {
      const result = await handleGithubCallback(code, installation_id);
      console.log('result', result);
      if (result && result.requiresMFA) {
        setMFAData({ email: result.email, qai: result.qai, code: result.code });
        setShowMFAPopup(true);
      }
    } catch (error) {
      console.log('error', error);
      console.error('GitHub callback error:', error);
      setError(error instanceof Error ? error.message : 'Authentication failed');
    }
  };

  const handleMFAVerification = async (token: string) => {
    if (!mfaData) return;
    try {
      const response = await MFAService.verifyMFA(
        mfaData.email,
        token,
        mfaData.code,
        'user_type',
        'github'
      );
      localStorage.setItem('authToken', `Bearer ${response.access_token}`);
      setShowMFAPopup(false);
      setMFAData(null);
      navigate('/sites');
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const handleMFACancel = () => {
    setShowMFAPopup(false);
    setMFAData(null);
    navigate('/');
  };

  // ── Error screen ──────────────────────────────────────────────────
  if (error) {
    return (
      <div
        className="flex h-screen items-center justify-center"
        style={{ background: '#09090e' }}
      >
        {/* Ambient glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '40%', left: '50%', transform: 'translate(-50%,-50%)',
            width: '360px', height: '360px', borderRadius: '50%',
            background: 'radial-gradient(circle,rgba(244,63,94,0.08),transparent)',
            filter: 'blur(60px)',
          }}
        />

        <div className="relative z-10 flex flex-col items-center gap-5 text-center px-6 max-w-sm">
          {/* Logo */}
          <div
            className="w-12 h-12 rounded-[12px] flex items-center justify-center font-black text-[15px] text-white"
            style={{
              background: 'linear-gradient(135deg,#6366f1,#a855f7)',
              fontFamily: "'Syne',sans-serif",
            }}
          >
            Qx
          </div>

          {/* Error icon */}
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(244,63,94,0.1)',
              border: '1px solid rgba(244,63,94,0.3)',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#f43f5e" strokeWidth="1.5">
              <circle cx="10" cy="10" r="8.5" />
              <path d="M10 6v4M10 13v.5" />
            </svg>
          </div>

          <div>
            <h2
              className="text-[20px] font-bold mb-1.5"
              style={{ fontFamily: "'Syne',sans-serif", color: '#eeeef5' }}
            >
              Authentication Error
            </h2>
            <p className="text-[13px] mb-2" style={{ color: '#8e8ea8' }}>{error}</p>
            <p className="text-[12px]" style={{ color: '#52525f' }}>
              Redirecting to login page…
            </p>
          </div>

          {/* Redirect progress bar */}
          <div
            className="w-full h-[2px] rounded-full overflow-hidden"
            style={{ background: '#26262f' }}
          >
            <div
              className="h-full rounded-full"
              style={{
                background: '#f43f5e',
                animation: 'shrink 3s linear forwards',
                width: '100%',
              }}
            />
          </div>

          <button
            onClick={() => navigate('/')}
            className="text-[12.5px] underline transition-colors"
            style={{ color: '#818cf8' }}
          >
            Go back now
          </button>
        </div>

        <style>{`
          @keyframes shrink {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
      </div>
    );
  }

  // ── Loading screen ────────────────────────────────────────────────
  return (
    <>
      <AuthLoadingScreen />

      {showMFAPopup && mfaData && (
        <MFAVerificationPopup
          qrCode={mfaData.qai}
          email={mfaData.email}
          onSubmit={handleMFAVerification}
          onCancel={handleMFACancel}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default GitHubCallback;